import React, { useMemo } from "react";
import styles from "../App.module.css";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Logo from "./Logo";

// ─── Constants ───────────────────────────────────────────────────────────────
const DOMAIN = "https://www.satah-alkhobar.com";
const AR_URL = `${DOMAIN}/`;
const EN_URL = `${DOMAIN}/?lang=en`;
const PHONE = "+966507688086";
const PHONE_DISPLAY = "0507688086";
const EMAIL = "alkhobarsatah@gmail.com";
const OG_IMAGE = DOMAIN + "/logo512.png";
const GEO = { lat: 26.2172, lng: 50.1971 };

// ─── SEO Config ──────────────────────────────────────────────────────────────
const SEO = {
  ar: {
    title: "سطحة هيدروليك الخبر",
    description: `سطحة هيدروليك الخبر - أفضل خدمة سطحة وسحب سيارات في الخبر والدمام والظهران. ننقل سيارتك من الخبر إلى البحرين عبر جسر الملك فهد وحدود الكويت والإمارات. استجابة 10 دقائق، أسعار مناسبة. اتصل الآن ${PHONE_DISPLAY}`,
    keywords:
      "سطحة الخبر, سطحة هيدروليك الخبر, سطحة الدمام, سطحة الظهران, سحب سيارات الخبر, سطحة هيدروليك, نقل سيارات الخبر, سطحة طوارئ الخبر, سطحة رخيصة الخبر, سطحة 24 ساعة الخبر, سطحة العزيزية, سطحة الثقبة, سطحة الجبيل, سطحة القطيف, سطحة رأس تنورة, سحب سيارات الدمام, سحب سيارات الظهران, سطحة هيدروليك الدمام, ونش الخبر, ونش سحب الخبر, سطحة نقل سيارات, نقل سيارات من الخبر للبحرين, سطحة جسر الملك فهد, سحب من الخبر للبحرين, سحب حدود الكويت, سحب حدود الإمارات, نقل سيارات الخفجي, satah alkhobar, towing khobar, towing to bahrain from khobar",
    url: AR_URL,
    image: OG_IMAGE,
    locale: "ar_SA",
  },
  en: {
    title: "Tow Truck Khobar | Satah Al-Khobar",
    description: `Al Khobar Hydraulic Towing - Based in Khobar. 24/7 flatbed towing across Dammam, Dhahran & Eastern Province. We transport vehicles from Khobar to Bahrain via King Fahd Causeway + Kuwait, UAE & Oman borders. Call ${PHONE_DISPLAY}`,
    keywords:
      "tow truck Khobar, towing Khobar, flatbed towing Khobar, سطحة الخبر, vehicle recovery Khobar, towing Dammam, towing Dhahran, emergency towing Khobar, car towing Eastern Province, hydraulic towing Saudi Arabia, Khobar tow truck service, 24/7 towing Khobar, affordable towing Khobar, vehicle transport Khobar to Bahrain, towing from Khobar to Bahrain, King Fahd Causeway towing, cross border towing from Khobar, towing to Kuwait border Khafji, satah alkhobar, satah al khobar",
    url: EN_URL,
    image: OG_IMAGE,
    locale: "en_US",
  },
};

// ─── Structured Data Builders ────────────────────────────────────────────────
const REVIEWS = [
  {
    author: "أحمد خان",
    rating: 5,
    body: "سطحة ممتازة جدًا في الخبر! وصلوا خلال 15 دقيقة وسحبوا سيارتي باحترافية.",
  },
  {
    author: "سعد العتيبي",
    rating: 5,
    body: "خدمة موثوقة وسريعة، والسائق كان محترم ومهذب جدًا.",
  },
  {
    author: "منصور الرحمن",
    rating: 5,
    body: "تعطلت سيارتي قرب كورنيش الخبر ليلًا، وصلوا بسرعة وساعدوني فورًا. خدمة رائعة بالفعل!",
  },
];

const AREA_SERVED = [
  { name: "الخبر", sameAs: "https://en.wikipedia.org/wiki/Khobar" },
  { name: "الدمام", sameAs: "https://en.wikipedia.org/wiki/Dammam" },
  { name: "الظهران", sameAs: "https://en.wikipedia.org/wiki/Dhahran" },
  { name: "الجبيل" },
  { name: "القطيف" },
  { name: "رأس تنورة" },
  { name: "العزيزية" },
  { name: "الثقبة" },
  { name: "البحرين", type: "Country", sameAs: "https://en.wikipedia.org/wiki/Bahrain" },
  { name: "المنامة", sameAs: "https://en.wikipedia.org/wiki/Manama" },
  { name: "الخفجي" },
  { name: "البطحاء" },
  { name: "الأحساء", sameAs: "https://en.wikipedia.org/wiki/Al-Ahsa" },
  { name: "الرياض", sameAs: "https://en.wikipedia.org/wiki/Riyadh" },
  { name: "جدة", sameAs: "https://en.wikipedia.org/wiki/Jeddah" },
  { name: "مكة المكرمة", sameAs: "https://en.wikipedia.org/wiki/Mecca" },
  { name: "المدينة المنورة", sameAs: "https://en.wikipedia.org/wiki/Medina" },
  { name: "الطائف", sameAs: "https://en.wikipedia.org/wiki/Ta%27if" },
  { name: "أبها", sameAs: "https://en.wikipedia.org/wiki/Abha" },
  { name: "تبوك", sameAs: "https://en.wikipedia.org/wiki/Tabuk" },
  { name: "ينبع", sameAs: "https://en.wikipedia.org/wiki/Yanbu" },
];

const SERVICE_TYPES = [
  "سطحة هيدروليك",
  "سحب سيارات",
  "نقل سيارات",
  "إنقاذ سيارات",
  "سطحة طوارئ",
  "سحب من الخبر للبحرين",
  "نقل سيارات البحرين",
  "سطحة جسر الملك فهد",
  "سحب حدود الكويت",
  "سحب حدود الإمارات",
  "Emergency Towing",
  "Flatbed Towing",
  "Accident Recovery",
  "Long Distance Towing",
  "Luxury Vehicle Transport",
  "International Towing from Khobar to Bahrain",
  "Vehicle Transport to Bahrain",
  "Cross-Border Towing from Khobar",
];

const FAQ_ITEMS = [
  {
    question: "كم من الوقت يستغرق وصولكم؟",
    answer:
      "داخل الخبر، نصل عادةً في غضون 10-20 دقيقة. للمناطق القريبة مثل الدمام والظهران، وقت الاستجابة 15-25 دقيقة. للمناطق الأخرى مثل الجبيل أو القطيف، وقت الاستجابة 30-45 دقيقة حسب موقعك بالضبط.",
  },
  {
    question: "ما أنواع السيارات التي تسحبونها؟",
    answer:
      "نتعامل مع جميع أنواع المركبات: السيارات الصغيرة، الدفع الرباعي، الشاحنات، السيارات الفاخرة، السيارات الرياضية، وحتى الدراجات النارية. سطحاتنا المسطحة تضمن نقلًا آمنًا لأي مركبة.",
  },
  {
    question: "هل تعملون على مدار الساعة؟",
    answer:
      "نعم! سطحة هيدروليك الخبر تعمل 24 ساعة في اليوم، 7 أيام في الأسبوع، بما في ذلك العطلات. نحن دائمًا مستعدون لمساعدتك.",
  },
  {
    question: "كم يستغرق وصول سطحة الخبر؟",
    answer: "نصل داخل الخبر خلال 10-15 دقيقة، وللدمام والظهران 15-25 دقيقة.",
  },
  {
    question: "هل سطحة هيدروليك الخبر متاحة 24 ساعة؟",
    answer: "نعم، نعمل على مدار الساعة 24/7 بما في ذلك العطلات والأعياد.",
  },
  {
    question: "ما هي أسعار سطحة الخبر؟",
    answer:
      "أسعارنا تنافسية وتعتمد على المسافة ونوع المركبة. اتصل للحصول على تسعير فوري بدون رسوم مخفية.",
  },
  {
    question: "How fast is Khobar tow truck response time?",
    answer:
      "Within Khobar, we arrive in 10-15 minutes. For Dammam and Dhahran, response time is 15-25 minutes.",
  },
];

function buildStructuredData(lang) {
  const reviews = REVIEWS.map((r) => ({
    "@type": "Review",
    reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
    author: { "@type": "Person", name: r.author },
    reviewBody: r.body,
  }));

  const areaServed = AREA_SERVED.map((a) => ({
    "@type": a.type || "City",
    name: a.name,
    ...(a.sameAs && { sameAs: a.sameAs }),
  }));

  return {
    "@context": "https://schema.org",
    // Array type: LocalBusiness unlocks review, aggregateRating, openingHoursSpecification.
    // TowingService alone does NOT support these fields — Google will flag them as invalid.
    "@type": ["LocalBusiness", "TowingService"],
    "@id": DOMAIN + "/#business",
    name: "سطحة هيدروليك الخبر",
    alternateName: ["Al Khobar Hydraulic Towing", "Satah Al Khobar", "سطحة الخبر"],
    image: [OG_IMAGE],
    url: lang === "en" ? EN_URL : AR_URL,
    telephone: PHONE,
    email: EMAIL,
    description: lang === "en" ? SEO.en.description : SEO.ar.description,
    slogan: "سطحة هيدروليك الخبر - ننطلق من الخبر وننقل سيارتك أينما تريد",
    priceRange: "$$",
    knowsLanguage: ["ar", "en", "ur"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "الخبر",
      addressLocality: "الخبر",
      addressRegion: "المنطقة الشرقية",
      postalCode: "31952",
      addressCountry: "SA",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: GEO.lat,
      longitude: GEO.lng,
    },
    review: reviews,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5",
      // Derived from the reviews array — update REVIEWS above to keep in sync
      reviewCount: String(REVIEWS.length),
    },
    areaServed,
    serviceType: SERVICE_TYPES,
    // openingHoursSpecification must be an array per schema.org spec
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday", "Tuesday", "Wednesday", "Thursday",
          "Friday", "Saturday", "Sunday",
        ],
        opens: "00:00",
        closes: "23:59", // Google's rich result tester rejects "24:00" in practice
      },
    ],
    potentialAction: [
      {
        "@type": "CallAction",
        target: `tel:${PHONE}`,
        name: "اتصل الآن - Call Now",
      },
      {
        "@type": "CommunicateAction",
        target: `https://wa.me/${PHONE.replace("+", "")}`,
        name: "واتساب - WhatsApp",
      },
    ],
    // Single consolidated FAQPage — removed the nested mainEntity inside TowingService
    // because search engines ignore nested FAQPage; the standalone block below is used instead
  };
}

function buildFaqStructuredData(lang) {
  const langFaqItems = lang === "en"
    ? FAQ_ITEMS.filter((item) => /[A-Za-z]/.test(item.question))
    : FAQ_ITEMS.filter((item) => /[\u0600-\u06FF]/.test(item.question));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": (lang === "en" ? EN_URL : AR_URL) + "#faq",
    inLanguage: lang,
    mainEntity: langFaqItems.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

function buildServiceStructuredData(lang) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "سطحة هيدروليك - Hydraulic Flatbed Towing",
    provider: {
      "@type": "TowingService",
      name: "سطحة هيدروليك الخبر",
      url: lang === "en" ? EN_URL : AR_URL,
    },
    areaServed: {
      "@type": "State",
      name: "المنطقة الشرقية - Eastern Province",
    },
    description:
      "خدمات سطحة هيدروليك وسحب سيارات احترافية في الخبر والدمام والظهران - Professional hydraulic flatbed towing",
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      availabilityStarts: "00:00",
      availabilityEnds: "23:59",
    },
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Header() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "ar").startsWith("en") ? "en" : "ar";

  const currentSEO = SEO[lang] ?? SEO.ar;

  // Memoized — rebuilt only when lang changes
  const structuredData = useMemo(() => buildStructuredData(lang), [lang]);
  const faqStructuredData = useMemo(() => buildFaqStructuredData(lang), [lang]);
  const serviceStructuredData = useMemo(() => buildServiceStructuredData(lang), [lang]);

  return (
    <header className={styles.header}>
      <Helmet>
        <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
        <title>{currentSEO.title}</title>
        <meta name="description" content={currentSEO.description} />
        <meta name="keywords" content={currentSEO.keywords} />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <link rel="canonical" href={currentSEO.url} />
        <meta name="author" content="سطحة هيدروليك الخبر" />
        <meta name="publisher" content="سطحة هيدروليك الخبر" />
        <meta name="theme-color" content="#1B5E20" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="format-detection" content="telephone=yes" />

        {/* Geo */}
        <meta name="geo.region" content="SA-04" />
        <meta name="geo.placename" content="الخبر, Khobar" />
        <meta name="geo.position" content={`${GEO.lat};${GEO.lng}`} />
        <meta name="ICBM" content={`${GEO.lat}, ${GEO.lng}`} />
        <meta name="DC.title" content={currentSEO.title} />
        <meta name="DC.description" content={currentSEO.description} />

        {/*
          hreflang — if you later add /en and /ar routes, update these hrefs.
          x-default signals the preferred fallback for unmatched locales.
        */}
        <link rel="alternate" hreflang="ar" href={AR_URL} />
        <link rel="alternate" hreflang="en" href={EN_URL} />
        <link rel="alternate" hreflang="x-default" href={AR_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentSEO.url} />
        <meta property="og:title" content={currentSEO.title} />
        <meta property="og:description" content={currentSEO.description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="سطحة هيدروليك الخبر - سحب سيارات" />
        <meta property="og:locale" content={currentSEO.locale} />
        <meta property="og:locale:alternate" content={lang === "ar" ? "en_US" : "ar_SA"} />
        <meta property="og:site_name" content="سطحة هيدروليك الخبر" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentSEO.title} />
        <meta name="twitter:description" content={currentSEO.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="سطحة هيدروليك الخبر" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceStructuredData)}</script>

        {/* Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      </Helmet>

      <div className={styles.overlay}>
        <Logo size={88} variant="full" className={styles.headerLogo} />
        <h1 className={styles.title}>{t("header.title")}</h1>
        <p className={styles.subtitle}>{t("header.subtitle")}</p>

        <div className={styles.contactInfo}>
          {/*
            Always use the PHONE constant for href (tel: protocol needs a real number).
            t("contact.phone") is used only for display text so translators can localise it.
          */}
          <p>
            📞 <a href={`tel:${PHONE}`}>{t("contact.phone")}</a>
          </p>
          <p>
            ✉️ <a href={`mailto:${EMAIL}`}>{t("contact.email")}</a>
          </p>
        </div>

        {/* Fallback for crawlers / users without JS */}
        <noscript>
          <p>
            📞 <a href={`tel:${PHONE}`}>{PHONE_DISPLAY}</a>
          </p>
          <p>
            ✉️ <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
          </p>
        </noscript>

        <a
          href={`tel:${PHONE}`}
          className={styles.ctaButton}
          onClick={(e) => {
            if (typeof window.gtag_report_conversion === "function") {
              e.preventDefault();
              window.gtag_report_conversion(`tel:${PHONE}`);
            }
          }}
        >
          {t("contact.call")}
        </a>
      </div>
    </header>
  );
}